import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, MouseEvent, ReactNode, RefObject } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import AppSelectBox from '../shared/AppSelectBox';
import RadioGroupField from '../form/RadioGroupField';
import { SECONDARY_BUTTON_SX } from '../shared/dialogActionsSx';
import { useDialogKeys } from '../../hooks/useDialogKeys';
import { useGlobalStore } from '../../stores/globalStore';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useNavContext } from '../../navigation/useNavSections';
import { useGettingStartedAccess } from '../../pages/getting-started/useGettingStartedAccess';
import { useGettingStarted } from '../../api/gettingStartedApi';
import { useStartScreen, useDefaultFacility } from '../../pages/settings/useStartDefaults';
import { START_FORM } from '../../auth/resolveStartRoute';
import { SUPPORT_EMAIL, SUPPORT_PHONE } from '../../constants/support';
import {
  START_HERE_SLUG,
  canOpenStep,
  firstTasks,
  nextActions,
  routeCheck,
  stepName,
  tourPages,
  type DesktopAnswer,
  type FirstTask,
  type TourPage,
} from './welcomeTourModel';
import {
  SAME,
  SAME_LEAD,
  SAME_PICTURE,
  DIFFERENT,
  NEW_ITEMS,
  NEW_LEAD,
  NEW_USER_ITEMS,
  articleTitle,
  resolvePicture,
  type TourItem,
  type TourPicture,
} from './welcomeTourContent';
import type { TourProgress } from './welcomeTourStore';
import './WelcomeTour.css';

interface Props {
  /** Finished on the last step, or skipped / closed part way. */
  onClose: (outcome: 'finished' | 'skipped') => void;
  /**
   * A link in the tour was followed (a help article, Getting Started). The host keeps `progress`, opens
   * `to` in this tab, and offers the way back to the same step.
   */
  onLeave: (progress: TourProgress, to: string) => void;
  /** Where to open: the step the person left from to read an article. Default: the start. */
  initial?: TourProgress | null;
}

/**
 * The welcome tour: a fixed-size dialog with the steps listed down the left, the step on the right
 * (the only part that scrolls) and the buttons along the bottom. WelcomeTourHost decides when it opens.
 */
export default function WelcomeTour({ onClose, onLeave, initial }: Props) {
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const firstName = useGlobalStore((s) => s.userFirstName);
  const ehrActive = useGlobalStore((s) => !!s.facilitySettings?.facilityEhrActive);
  const facilityId = useGlobalStore((s) => s.facilityId);
  const residentPlural = useGlobalStore((s) => s.residentPluralLabel) || 'Residents';

  const [answer, setAnswer] = useState<DesktopAnswer>(initial?.answer ?? null);
  const [index, setIndex] = useState(initial?.index ?? 0);
  const [visited, setVisited] = useState(initial?.visited ?? initial?.index ?? 0);
  // The steps that have been on screen: a check in the step list (a step jumped over has none).
  const [seen, setSeen] = useState<number[]>(initial?.seen ?? [initial?.index ?? 0]);
  const [selected, setSelected] = useState<Record<string, number>>(initial?.selected ?? {});
  const [lightbox, setLightbox] = useState<TourPicture | null>(null);

  // What the role can open: the menu's own rules (navigation/navConfig).
  const navCtx = useNavContext();
  const can = useMemo(() => routeCheck(navCtx), [navCtx]);

  // Getting Started, for those who may use it where it is on for this facility.
  const gsAllowed = useGettingStartedAccess();
  const { data: gs } = useGettingStarted(facilityId, gsAllowed);
  const gettingStarted = gsAllowed && !!gs?.enabled && !!gs.steps?.length;

  const tasks = useMemo(() => firstTasks(can, { ehrActive, gettingStarted }), [can, ehrActive, gettingStarted]);
  const actions = useMemo(() => nextActions(can, residentPlural), [can, residentPlural]);

  // Start page and default facility: the same choices and saves as My Settings.
  const start = useStartScreen();
  const facility = useDefaultFacility();
  const [startChoice, setStartChoice] = useState<number | null>(initial?.startChoice ?? null);
  const [facilityChoice, setFacilityChoice] = useState<number | null>(initial?.facilityChoice ?? null);
  const [saving, setSaving] = useState(false);
  const startValue = startChoice ?? (start.isLoading ? null : start.current ?? START_FORM.RESIDENTS);
  const facilityValue = facilityChoice ?? (facility.loading ? null : facility.current);
  const showFacility = facility.facilityCount > 1;

  const pages = tourPages(answer);
  const page: TourPage = pages[Math.min(index, pages.length - 1)];
  const isLast = index >= pages.length - 1;
  const counted = answer != null;

  // Each step's heading takes the focus, so a screen reader reads the new step from the top; the
  // step's content scrolls back to its top.
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    contentRef.current?.scrollTo?.(0, 0);
    const t = window.setTimeout(() => headingRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [page]);

  const skip = () => onClose('skipped');

  const saveSettings = async (): Promise<boolean> => {
    setSaving(true);
    const okStart = startValue == null ? true : await start.save(startValue);
    const okFacility = !showFacility || facilityValue == null ? true : await facility.save(facilityValue);
    setSaving(false);
    return okStart && okFacility; // the notification says what failed
  };

  const goTo = async (target: number) => {
    if (target === index || saving || !canOpenStep(target, { answer, visited, count: pages.length })) return;
    // Moving on from Your settings saves it, from the step list as from Continue.
    if (page === 'setup' && target > index && !(await saveSettings())) return;
    setIndex(target);
    setVisited((v) => Math.max(v, target));
    setSeen((s) => (s.includes(target) ? s : [...s, target]));
  };

  const finish = (to = '/') => {
    onClose('finished');
    navigate(to);
  };

  const next = async () => {
    if (isLast) {
      finish();
      return;
    }
    await goTo(index + 1);
  };
  const back = () => { void goTo(index - 1); };

  const chooseAnswer = (v: 'desktop' | 'new') => {
    if (v === answer) return;
    // The path changes, so the steps after this one start again.
    setAnswer(v);
    setVisited(0);
    setSeen([0]);
    setSelected({});
  };

  /** A link in the tour: open it in this tab and keep the place, so "Back to the tour" returns here. */
  const leave = (to: string) => onLeave({ answer, index, visited, seen, selected, startChoice, facilityChoice }, to);

  const nextDisabled = (page === 'welcome' && answer == null) || (page === 'setup' && (start.isLoading || facility.loading));

  // Enter = Continue (not from a button, link, tab or list); Esc is the Dialog's own onClose (skip).
  const footRef = useRef<HTMLDivElement | null>(null);
  useDialogKeys(footRef, {
    onSave: () => { void next(); },
    saveDisabled: nextDisabled || !!lightbox,
    saving,
    scope: 'nearest',
    escape: 'never',
  });

  const select = (key: string) => (i: number) => setSelected((s) => ({ ...s, [key]: i }));

  let body: ReactNode;
  switch (page) {
    case 'welcome':
      body = (
        <>
          <Heading refEl={headingRef}>{firstName ? `Welcome to Dietech Online, ${firstName}` : 'Welcome to Dietech Online'}</Heading>
          <p className="wt-lead">This short tour shows you around and where to find help.</p>
          <RadioGroupField<'desktop' | 'new'>
            ariaLabel="Have you used Dietech before?"
            direction="column"
            value={answer}
            onValueChanged={chooseAnswer}
            groupClassName="wt-choices"
            optionClassName="wt-choice"
            options={[
              { value: 'desktop', label: 'I\'ve used Dietech on the desktop' },
              { value: 'new', label: 'I\'m new to Dietech' },
            ]}
          />
        </>
      );
      break;
    case 'same': {
      const picture = resolvePicture(SAME_PICTURE);
      body = (
        <>
          <Heading refEl={headingRef}>What stays the same</Heading>
          <p className="wt-lead">{SAME_LEAD}</p>
          <div className="wt-same">
            <Rows items={SAME} onLeave={leave} />
            {picture && <Shot picture={picture} onEnlarge={setLightbox} small />}
          </div>
        </>
      );
      break;
    }
    case 'different':
      body = (
        <>
          <Heading refEl={headingRef}>What's different</Heading>
          <FeatureList
            id="different"
            label="What's different"
            items={DIFFERENT}
            phone={isMobile}
            selected={selected.different ?? 0}
            onSelect={select('different')}
            onLeave={leave}
            onEnlarge={setLightbox}
          />
        </>
      );
      break;
    case 'new':
      body = (
        <>
          <Heading refEl={headingRef}>{stepName('new', answer)}</Heading>
          {answer === 'desktop' && <p className="wt-lead">{NEW_LEAD}</p>}
          <FeatureList
            id="new"
            label={stepName('new', answer)}
            items={answer === 'desktop' ? NEW_ITEMS : NEW_USER_ITEMS}
            phone={isMobile}
            selected={selected.new ?? 0}
            onSelect={select('new')}
            onLeave={leave}
            onEnlarge={setLightbox}
          />
        </>
      );
      break;
    case 'job':
      body = (
        <>
          <Heading refEl={headingRef}>What to do first</Heading>
          <p className="wt-lead">
            Start with <ReadLink slug={START_HERE_SLUG} onLeave={leave} bare />. It shows where everything is.
          </p>
          {/* Never empty: everyone gets Your settings. */}
          <ul className="wt-rows">
            {tasks.map((t) => <TaskRow key={t.id} task={t} onLeave={leave} />)}
          </ul>
        </>
      );
      break;
    case 'setup':
      body = (
        <>
          <Heading refEl={headingRef}>Your settings</Heading>
          <p className="wt-lead">Saved when you select Continue. You can change them later in My Settings.</p>
          <div className="wt-fields">
            <AppSelectBox
              label="Start page"
              dataSource={start.options}
              displayExpr="name"
              valueExpr="id"
              value={startValue}
              onValueChanged={(v) => { if (v != null) setStartChoice(Number(v)); }}
              showClearButton={false}
              searchEnabled={false}
              disabled={start.isLoading || saving}
            />
            {showFacility && (
              <AppSelectBox
                label="Default facility"
                dataSource={facility.options}
                displayExpr="name"
                valueExpr="id"
                value={facilityValue}
                onValueChanged={(v) => { if (v != null) setFacilityChoice(Number(v)); }}
                showClearButton={false}
                searchEnabled={facility.options.length > 8}
                disabled={facility.loading || saving}
              />
            )}
          </div>
        </>
      );
      break;
    case 'ready':
      body = (
        <>
          <Heading refEl={headingRef}>You're ready to go</Heading>
          <p className="wt-lead">The tour is done. You can open it again any time from Help, under Help Articles.</p>
          <div className="wt-actions" role="group" aria-label="Open a page">
            {actions.map((a) => (
              <Button key={a.to} variant="outlined" sx={SECONDARY_BUTTON_SX} onClick={() => finish(a.to)}>{a.label}</Button>
            ))}
          </div>
          <p className="wt-support">
            Dietech support: {SUPPORT_PHONE} or <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            Help, under Support Hours, shows when support is open.
          </p>
        </>
      );
      break;
  }

  const stepCount = counted ? `Step ${index + 1} of ${pages.length}` : 'Step 1';

  return (
    <Dialog
      open
      onClose={skip}
      fullScreen={isMobile}
      maxWidth={false}
      aria-labelledby="welcome-tour-heading"
      className="welcome-tour"
      PaperProps={{ className: 'wt-paper' }}
    >
      <div className="wt-head">
        <span className="wt-head__title">Welcome to Dietech Online</span>
        <span className="wt-head__count" aria-live="polite">{stepCount}</span>
        <IconButton size="small" aria-label="Close the tour" onClick={skip} className="wt-head__close">
          <i className="bi bi-x-lg" aria-hidden="true" />
        </IconButton>
      </div>
      {isMobile && (
        <ol className="wt-strip" aria-hidden="true">
          {pages.map((p, i) => <li key={p} className={i <= index ? 'wt-strip__seg wt-strip__seg--done' : 'wt-strip__seg'} />)}
        </ol>
      )}
      <div className="wt-main">
        {!isMobile && (
          <nav className="wt-steps" aria-label="Tour steps">
            <ol>
              {pages.map((p, i) => {
                const current = i === index;
                const done = !current && seen.includes(i);
                const openable = canOpenStep(i, { answer, visited, count: pages.length });
                return (
                  <li key={p}>
                    <button
                      type="button"
                      className={['wt-step', current && 'wt-step--current', done && 'wt-step--done'].filter(Boolean).join(' ')}
                      aria-current={current ? 'step' : undefined}
                      aria-label={done ? `${stepName(p, answer)}, completed` : undefined}
                      disabled={!openable || saving}
                      onClick={() => { void goTo(i); }}
                    >
                      <span className="wt-step__mark" aria-hidden="true">
                        {done ? <i className="bi bi-check-lg" /> : i + 1}
                      </span>
                      <span className="wt-step__name">{stepName(p, answer)}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
            <p className="wt-steps__count">{counted ? `${index + 1} of ${pages.length} steps` : ''}</p>
          </nav>
        )}
        <div className="wt-content" ref={contentRef}>{body}</div>
      </div>
      <div className="wt-foot" ref={footRef}>
        <Button variant="text" onClick={skip} disabled={saving} sx={SECONDARY_BUTTON_SX}>Skip tour</Button>
        <span className="wt-foot__right">
          <Button variant="outlined" onClick={back} disabled={index === 0 || saving} sx={SECONDARY_BUTTON_SX}>Back</Button>
          <Button variant="contained" onClick={() => { void next(); }} disabled={nextDisabled || saving} aria-busy={saving || undefined}>
            {saving ? 'Saving...' : isLast ? 'Get started' : 'Continue'}
          </Button>
        </span>
      </div>
      {lightbox && <Lightbox picture={lightbox} onClose={() => setLightbox(null)} />}
    </Dialog>
  );
}

function Heading({ refEl, children }: { refEl: RefObject<HTMLHeadingElement | null>; children: ReactNode }) {
  return (
    <h2 id="welcome-tour-heading" ref={refEl} tabIndex={-1} className="wt-heading">
      {children}
    </h2>
  );
}

/** Compact rows: a small icon, the bold name, a sentence or two and "Read: <article>". */
function Rows({ items, onLeave }: { items: TourItem[]; onLeave: (to: string) => void }) {
  return (
    <ul className="wt-rows">
      {items.map((item) => (
        <li key={item.name} className="wt-row">
          <i className={`bi ${item.icon} wt-row__icon`} aria-hidden="true" />
          <div>
            <strong className="wt-row__name">{item.name}</strong>
            <p>
              {item.text}
              {item.read && <> <ReadLink slug={item.read} onLeave={onLeave} /></>}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function TaskRow({ task, onLeave }: { task: FirstTask; onLeave: (to: string) => void }) {
  return (
    <li className="wt-row wt-row--task">
      <div>
        <strong className="wt-row__name">{task.label}</strong>
        <p>
          {task.text}{' '}
          {task.slug
            ? <ReadLink slug={task.slug} onLeave={onLeave} />
            : task.to && <LeaveLink to={task.to} onLeave={onLeave}>{task.linkText ?? task.label}</LeaveLink>}
        </p>
      </div>
    </li>
  );
}

/**
 * A selectable list: the names down the left (a vertical tab list: arrow keys move, the selected one
 * shows), the selected item on the right with its picture. The detail has a fixed picture frame, so
 * switching items does not change the height.
 */
function FeatureList({ id, label, items, phone, selected, onSelect, onLeave, onEnlarge }: {
  id: string;
  label: string;
  items: TourItem[];
  phone: boolean;
  selected: number;
  onSelect: (i: number) => void;
  onLeave: (to: string) => void;
  onEnlarge: (p: TourPicture) => void;
}) {
  const sel = Math.min(Math.max(selected, 0), items.length - 1);
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  const item = items[sel];
  const phoneVersion = phone && !!item.phoneText;
  const picture = phoneVersion ? undefined : resolvePicture(item.picture);

  const onKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    const last = items.length - 1;
    const to = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? (sel >= last ? 0 : sel + 1)
      : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? (sel <= 0 ? last : sel - 1)
        : e.key === 'Home' ? 0
          : e.key === 'End' ? last
            : -1;
    if (to < 0) return;
    e.preventDefault();
    onSelect(to);
    tabs.current[to]?.focus();
  };

  return (
    <div className="wt-features">
      <div className="wt-features__list" role="tablist" aria-orientation="vertical" aria-label={label}>
        {items.map((it, i) => {
          const usePhone = phone && !!it.phoneText;
          return (
            <button
              key={it.name}
              ref={(el) => { tabs.current[i] = el; }}
              type="button"
              role="tab"
              id={`wt-tab-${id}-${i}`}
              aria-selected={i === sel}
              aria-controls={`wt-panel-${id}`}
              tabIndex={i === sel ? 0 : -1}
              className={i === sel ? 'wt-feature wt-feature--selected' : 'wt-feature'}
              onClick={() => onSelect(i)}
              onKeyDown={onKey}
            >
              <i className={`bi ${it.icon} wt-feature__icon`} aria-hidden="true" />
              <span className="wt-feature__text">
                <span className="wt-feature__name">{usePhone ? it.phoneName ?? it.name : it.name}</span>
                <span className="wt-feature__short">{usePhone ? it.phoneShort ?? it.short : it.short}</span>
              </span>
            </button>
          );
        })}
      </div>
      <div
        className="wt-features__detail"
        role="tabpanel"
        id={`wt-panel-${id}`}
        aria-labelledby={`wt-tab-${id}-${sel}`}
        key={sel}
      >
        <h3 className="wt-features__title">{phoneVersion ? item.phoneName ?? item.name : item.name}</h3>
        <p>
          {phoneVersion ? item.phoneText : item.text}
          {item.read && <> <ReadLink slug={item.read} onLeave={onLeave} /></>}
        </p>
        {picture && <Shot picture={picture} onEnlarge={onEnlarge} />}
      </div>
    </div>
  );
}

/** A help article's still in the tour's one frame (same aspect ratio everywhere), with its caption. Selecting it enlarges it. */
function Shot({ picture, onEnlarge, small }: { picture: TourPicture; onEnlarge: (p: TourPicture) => void; small?: boolean }) {
  return (
    <figure className={small ? 'wt-shot wt-shot--small' : 'wt-shot'}>
      <button type="button" className="wt-shot__frame" onClick={() => onEnlarge(picture)} aria-label={`Enlarge picture: ${picture.alt}`}>
        <img
          className="wt-shot__img"
          src={picture.src}
          alt={picture.alt}
          width={picture.width}
          height={picture.height}
          loading="lazy"
          decoding="async"
        />
        <i className="bi bi-arrows-angle-expand wt-shot__hint" aria-hidden="true" />
      </button>
      <figcaption className="wt-shot__caption">{picture.caption}</figcaption>
    </figure>
  );
}

/** The picture at full size, over the tour. Esc or Close shuts it and the focus goes back to the picture. */
function Lightbox({ picture, onClose }: { picture: TourPicture; onClose: () => void }) {
  return (
    <Dialog open onClose={onClose} maxWidth={false} className="wt-lightbox" aria-labelledby="wt-lightbox-caption">
      <figure className="wt-lightbox__figure">
        <img src={picture.src} alt={picture.alt} width={picture.width} height={picture.height} className="wt-lightbox__img" />
        <figcaption id="wt-lightbox-caption" className="wt-lightbox__caption">{picture.caption}</figcaption>
      </figure>
      <div className="wt-lightbox__foot">
        <Button variant="contained" onClick={onClose}>Close</Button>
      </div>
    </Dialog>
  );
}

/** "Read: <article title>", or just the title (`bare`). Opens in this tab; the tour waits. */
function ReadLink({ slug, onLeave, bare }: { slug: string; onLeave: (to: string) => void; bare?: boolean }) {
  const title = articleTitle(slug) ?? slug;
  return (
    <LeaveLink to={`/help/${slug}`} onLeave={onLeave}>
      {bare ? title : `Read: ${title}`}
    </LeaveLink>
  );
}

function LeaveLink({ to, onLeave, children }: { to: string; onLeave: (to: string) => void; children: ReactNode }) {
  return (
    <Link
      to={to}
      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        onLeave(to);
      }}
    >
      {children}
    </Link>
  );
}
